import React from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Iconify from "../../iconify/Iconify";

const VISIBILITY_OPTIONS = [
  {
    value: false,
    title: "Intern",
    description:
      "Das Event ist nur intern sichtbar. Öffentliche Anmeldung ist nicht verfügbar.",
    icon: "solar:shield-warning-bold-duotone",
    color: "#64748b",
  },
  {
    value: true,
    title: "Öffentlich",
    description:
      "Das Event wird im Mitgliederbereich angezeigt und kann optional öffentlich buchbar sein.",
    icon: "solar:eye-bold-duotone",
    color: "#15803d",
  },
];

const PRESET_OPTIONS = [
  { value: "seminar", label: "Seminar" },
  { value: "ferienbetreuung", label: "Ferienbetreuung" },
  { value: "camp_uebernachtung", label: "Camp" },
  { value: "reisecamp", label: "Reisecamp" },
  { value: "custom", label: "Individuell" },
];

const CAPACITY_MODE_OPTIONS = [
  {
    value: "registrations",
    label: "Nach Anmeldungen",
  },
  {
    value: "participants",
    label: "Nach Teilnehmern",
  },
];

const isValidDateTimeLocal = (value) => {
  if (!value) return true;
  return dayjs(value).isValid();
};

export default function StepRegistration({ formData, errors, onChange }) {
  const isPublic = Boolean(formData.isPublic);
  const registrationConfig = formData.registrationConfig || {};

  const updateRegistrationConfig = (patch) => {
    onChange("registrationConfig", {
      ...registrationConfig,
      ...patch,
    });
  };

  const handleVisibilityChange = (value) => {
    onChange("isPublic", value);
    if (!value && registrationConfig.isPublicRegistrationEnabled) {
      updateRegistrationConfig({ isPublicRegistrationEnabled: false });
    }
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">Anmeldung & Sichtbarkeit</Typography>
        <Typography variant="body2" color="text.secondary">
          Definieren Sie den Buchungsmodus, Fristen und Limits für das Buchungs-Event.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        {VISIBILITY_OPTIONS.map((option) => {
          const isActive = isPublic === option.value;
          return (
            <Paper
              key={option.title}
              variant="outlined"
              role="button"
              tabIndex={0}
              aria-pressed={isActive}
              onClick={() => handleVisibilityChange(option.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleVisibilityChange(option.value);
                }
              }}
              sx={{
                p: 2,
                borderWidth: isActive ? 2 : 1,
                borderColor: isActive ? option.color : "divider",
                bgcolor: isActive ? alpha(option.color, 0.1) : "background.paper",
                cursor: "pointer",
                transition: "all 160ms ease",
                "&:hover": {
                  borderColor: option.color,
                  bgcolor: alpha(option.color, 0.08),
                },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(option.color, isActive ? 0.5 : 0.3)}`,
                    bgcolor: alpha(option.color, isActive ? 0.18 : 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Iconify icon={option.icon} width={22} sx={{ color: option.color }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {option.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {option.description}
                  </Typography>
                </Box>
                {isActive ? (
                  <Iconify
                    icon="solar:check-circle-bold"
                    width={20}
                    sx={{ color: option.color, mt: 0.25 }}
                  />
                ) : null}
              </Stack>
            </Paper>
          );
        })}
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(registrationConfig.isPublicRegistrationEnabled)}
                onChange={(event) =>
                  updateRegistrationConfig({
                    isPublicRegistrationEnabled: event.target.checked,
                  })
                }
                color="secondary"
                disabled={!isPublic}
              />
            }
            label="Öffentliche Anmeldung ohne Login erlauben"
          />

          {!isPublic ? (
            <Alert severity="info">
              Öffentliche Anmeldung ist nur möglich, wenn das Event sichtbar (öffentlich) ist.
            </Alert>
          ) : (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
                Aktiviert den öffentlichen Buchungslink `/public/event/:eventId`.
              </Typography>
              {Boolean(registrationConfig.isPublicRegistrationEnabled) ? (
                <Alert severity="info">
                  Nach erfolgreicher öffentlicher Anmeldung wird automatisch
                  eine Bestätigungsmail an den Buchenden gesendet. Den Mailtext
                  können Sie im Schritt „Extras" anpassen.
                </Alert>
              ) : null}
            </Stack>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={Boolean(registrationConfig.requiresApproval)}
                onChange={(event) =>
                  updateRegistrationConfig({
                    requiresApproval: event.target.checked,
                  })
                }
                color="secondary"
              />
            }
            label="Manuelle Freigabe jeder Buchung erforderlich"
          />

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Anmeldefrist"
                type="datetime-local"
                value={registrationConfig.registrationDeadline || ""}
                onChange={(event) =>
                  updateRegistrationConfig({
                    registrationDeadline: event.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.registrationConfig)}
                helperText={
                  errors.registrationConfig ||
                  "Nach diesem Zeitpunkt sind keine neuen Buchungen mehr möglich."
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Max. Anmeldungen (leer = kein Limit)"
                type="number"
                value={registrationConfig.maxRegistrations ?? ""}
                onChange={(event) =>
                  updateRegistrationConfig({
                    maxRegistrations: event.target.value,
                  })
                }
                inputProps={{ min: 1 }}
                fullWidth
              />
            </Grid>
          </Grid>

          <FormControl>
            <FormLabel>Kapazitätsmodus</FormLabel>
            <RadioGroup
              row
              value={registrationConfig.capacityMode || "registrations"}
              onChange={(event) =>
                updateRegistrationConfig({
                  capacityMode: event.target.value,
                })
              }
            >
              {CAPACITY_MODE_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio color="secondary" />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <TextField
            label="Eventvorlage"
            select
            value={registrationConfig.eventPreset || "custom"}
            onChange={(event) =>
              updateRegistrationConfig({
                eventPreset: event.target.value,
              })
            }
            fullWidth
          >
            {PRESET_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      {registrationConfig.registrationDeadline &&
      !isValidDateTimeLocal(registrationConfig.registrationDeadline) ? (
        <Alert severity="error">
          Die Anmeldefrist hat ein ungültiges Datumsformat.
        </Alert>
      ) : null}
    </Stack>
  );
}

StepRegistration.propTypes = {
  formData: PropTypes.shape({
    isPublic: PropTypes.bool,
    registrationConfig: PropTypes.shape({
      isPublicRegistrationEnabled: PropTypes.bool,
      requiresApproval: PropTypes.bool,
      registrationDeadline: PropTypes.string,
      maxRegistrations: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      eventPreset: PropTypes.string,
      capacityMode: PropTypes.string,
    }),
  }).isRequired,
  errors: PropTypes.shape({
    registrationConfig: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};
